import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import {
  Terminal, Search, Calendar, User, Monitor, ChevronLeft, ChevronRight,
  Clock, Command, AlertTriangle, Filter, X, Eye
} from 'lucide-react';
import { toast } from 'sonner';

export default function TerminalSessionViewer() {
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ user_email: '', device_id: '', date_from: '', date_to: '' });
  const [activeFilters, setActiveFilters] = useState({});
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pageSize = 20;

  const loadSessions = useCallback(async (f = activeFilters, p = page) => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: p * pageSize };
      if (f.user_email) params.user_email = f.user_email;
      if (f.device_id) params.device_id = f.device_id;
      if (f.date_from) params.date_from = new Date(f.date_from).toISOString();
      if (f.date_to) params.date_to = new Date(f.date_to + 'T23:59:59').toISOString();
      const res = await api.get('/ninja-remote/terminal-sessions', { params });
      setSessions(res.data.sessions || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load terminal sessions');
    }
    setLoading(false);
  }, [activeFilters, page]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const applyFilters = () => {
    const f = {};
    if (filters.user_email.trim()) f.user_email = filters.user_email.trim();
    if (filters.device_id.trim()) f.device_id = filters.device_id.trim();
    if (filters.date_from) f.date_from = filters.date_from;
    if (filters.date_to) f.date_to = filters.date_to;
    setActiveFilters(f);
    setPage(0);
    loadSessions(f, 0);
  };

  const clearFilters = () => {
    setFilters({ user_email: '', device_id: '', date_from: '', date_to: '' });
    setActiveFilters({});
    setPage(0);
    loadSessions({}, 0);
  };

  const openDetail = async (session) => {
    if (session.entries && session.entries.length > 0) {
      setSelectedSession(session);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await api.get(`/ninja-remote/terminal-sessions/${session.id}`);
      setSelectedSession(res.data);
    } catch {
      toast.error('Failed to load session details');
    }
    setDetailLoading(false);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  const statusIcon = (status) => {
    if (status === 'completed') return <span className="text-green-500">●</span>;
    if (status === 'error' || status === 'failed') return <span className="text-red-500">●</span>;
    if (status === 'blocked') return <AlertTriangle className="h-3 w-3 text-amber-500 inline" />;
    return <span className="text-gray-400">●</span>;
  };

  return (
    <div className="space-y-6" data-testid="terminal-sessions-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Terminal Sessions</h1>
        <p className="text-gray-600 text-sm mt-1">Audit log of all terminal commands executed on remote devices</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-gray-500 mb-1 block">User</label>
              <div className="relative">
                <User className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={filters.user_email}
                  onChange={(e) => setFilters(p => ({ ...p, user_email: e.target.value }))}
                  placeholder="Filter by email..."
                  className="pl-8 h-9"
                  data-testid="filter-user-email"
                />
              </div>
            </div>
            <div className="w-[140px]">
              <label className="text-xs text-gray-500 mb-1 block">Device ID</label>
              <div className="relative">
                <Monitor className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={filters.device_id}
                  onChange={(e) => setFilters(p => ({ ...p, device_id: e.target.value }))}
                  placeholder="Device ID..."
                  className="pl-8 h-9"
                  data-testid="filter-device-id"
                />
              </div>
            </div>
            <div className="w-[160px]">
              <label className="text-xs text-gray-500 mb-1 block">From</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(p => ({ ...p, date_from: e.target.value }))}
                className="h-9"
                data-testid="filter-date-from"
              />
            </div>
            <div className="w-[160px]">
              <label className="text-xs text-gray-500 mb-1 block">To</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(p => ({ ...p, date_to: e.target.value }))}
                className="h-9"
                data-testid="filter-date-to"
              />
            </div>
            <Button size="sm" onClick={applyFilters} className="h-9" data-testid="apply-filters-btn">
              <Filter className="h-4 w-4 mr-1" /> Filter
            </Button>
            {hasActiveFilters && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="h-9" data-testid="clear-filters-btn">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Bar */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{total} session{total !== 1 ? 's' : ''} found{hasActiveFilters ? ' (filtered)' : ''}</span>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs">Page {page + 1} of {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Sessions List */}
      {loading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16 text-gray-500">
            <Terminal className="h-6 w-6 animate-pulse mr-3" /> Loading sessions...
          </CardContent>
        </Card>
      ) : sessions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-gray-500">
            <Terminal className="h-12 w-12 text-gray-300 mb-3" />
            <p className="font-medium">No terminal sessions found</p>
            <p className="text-sm mt-1">Sessions are recorded when admins use the terminal on device pages</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="sessions-list">
          {sessions.map((session) => (
            <Card
              key={session.id}
              className="hover:border-gray-300 cursor-pointer transition-colors"
              onClick={() => openDetail(session)}
              data-testid={`session-${session.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                      <Terminal className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{session.asset_name || `Device #${session.device_id}`}</span>
                        <Badge variant="outline" className="text-[10px]">{session.command_count || 0} cmd{(session.command_count || 0) !== 1 ? 's' : ''}</Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" /> {session.user_email || 'Unknown'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {new Date(session.created_at).toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Monitor className="h-3 w-3" /> ID: {session.device_id}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Eye className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Session Detail Dialog */}
      <Dialog open={!!selectedSession} onOpenChange={(open) => { if (!open) setSelectedSession(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="session-detail-dialog">
          {selectedSession && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Terminal className="h-5 w-5" />
                  Session: {selectedSession.asset_name || `Device #${selectedSession.device_id}`}
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">User</div>
                  <div className="font-medium text-sm mt-0.5 truncate">{selectedSession.user_email}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Device</div>
                  <div className="font-medium text-sm mt-0.5">{selectedSession.device_id}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Commands</div>
                  <div className="font-medium text-sm mt-0.5">{selectedSession.command_count || 0}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">Date</div>
                  <div className="font-medium text-sm mt-0.5">{new Date(selectedSession.created_at).toLocaleString()}</div>
                </div>
              </div>

              <Separator />

              {/* Terminal-style command log */}
              <div className="rounded-lg overflow-hidden border border-gray-700 bg-[#1a1b26]">
                <div className="px-3 py-1.5 bg-[#24283b] border-b border-gray-700 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 opacity-70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 opacity-70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 opacity-70" />
                  </div>
                  <span className="text-gray-500 text-xs ml-1">Session Playback</span>
                </div>
                <div className="p-3 max-h-[400px] overflow-y-auto font-mono text-[13px] leading-relaxed" data-testid="session-command-log">
                  {(!selectedSession.entries || selectedSession.entries.length === 0) ? (
                    <div className="text-gray-500 text-center py-8">No commands recorded in this session</div>
                  ) : (
                    selectedSession.entries.map((entry, i) => (
                      <div key={i} className="mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-cyan-400 select-none">$</span>
                          <span className="text-gray-200">{entry.command}</span>
                          <span className="text-gray-600 text-[10px] ml-auto">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ''}
                          </span>
                          {statusIcon(entry.status)}
                        </div>
                        {entry.output && (
                          <div className="text-green-400 whitespace-pre-wrap ml-4 mt-0.5 text-xs opacity-80">
                            {entry.output.length > 2000 ? entry.output.slice(0, 2000) + '\n... (truncated)' : entry.output}
                          </div>
                        )}
                        {entry.status === 'blocked' && (
                          <div className="text-amber-400 ml-4 mt-0.5 text-xs">BLOCKED: Command restricted</div>
                        )}
                        {entry.status === 'error' && (
                          <div className="text-red-400 ml-4 mt-0.5 text-xs">{entry.output || 'Error'}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
